import { Box, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { StarRating } from './StarRating';
import type { ReviewResponse } from '@lov/shared';
import { formatDate as formatDateIST } from '@lov/shared';

interface ReviewCardProps {
  review: ReviewResponse;
}

// Styled Components
const Card = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  padding: 20,
  marginBottom: 16,
}));

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 12,
});

const CustomerInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const CustomerName = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontFamily: '"IBM Plex Sans", sans-serif',
}));

const ReviewDate = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  fontFamily: '"IBM Plex Sans", sans-serif',
}));

const CommentText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  lineHeight: 1.6,
  color: theme.palette.text.primary,
  marginTop: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const ImagesContainer = styled(Box)({
  display: 'flex',
  gap: 8,
  marginTop: 12,
  flexWrap: 'wrap',
});

const ReviewImage = styled('img')({
  width: 80,
  height: 80,
  borderRadius: 8,
  objectFit: 'cover',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

/**
 * ReviewCard - Display a single review
 *
 * Shows customer name, rating, date, comment, and optional images.
 * Displays "Edited" badge if the review was modified.
 *
 * @example
 * <ReviewCard review={reviewData} />
 */
export function ReviewCard({ review }: ReviewCardProps) {
  const reviewDate = new Date(review.createdAt);
  const wasEdited = new Date(review.updatedAt).getTime() > reviewDate.getTime();

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

    return formatDateIST(date);
  };

  return (
    <Card>
      <Header>
        <CustomerInfo>
          <CustomerName>{review.customer.displayName}</CustomerName>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarRating rating={review.rating} size={16} />
            <ReviewDate>{formatDate(reviewDate)}</ReviewDate>
            {wasEdited && (
              <Chip
                label="Edited"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  backgroundColor: 'action.hover',
                  color: 'text.secondary',
                }}
              />
            )}
          </Box>
        </CustomerInfo>
      </Header>

      {review.comment && <CommentText>{review.comment}</CommentText>}

      {review.images && review.images.length > 0 && (
        <ImagesContainer>
          {review.images.map((imageUrl, index) => (
            <ReviewImage
              key={index}
              src={imageUrl}
              alt={`Review image ${index + 1}`}
              loading="lazy"
            />
          ))}
        </ImagesContainer>
      )}
    </Card>
  );
}
